import React, { useState, useRef, useEffect } from "react";
import html2canvas from "html2canvas";
import Navbar from "../common/Navbar";
import Footer from "../common/Footer";
import Sidebar from "../common/Sidebar";
import "./Whiteboard.css";

const Whiteboard = () => {
  const [brushSize, setBrushSize] = useState(5);
  const [brushColor, setBrushColor] = useState("#000000");
  const [drawing, setDrawing] = useState(false);
  const [mode, setMode] = useState("freehand");
  const [startPos, setStartPos] = useState(null);
  const [elements, setElements] = useState([]); // Store all drawn elements (shapes, paths, images)
  const [selectedImage, setSelectedImage] = useState(null); // For image moving
  const [draggingImage, setDraggingImage] = useState(false); // Track if an image is being dragged
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 }); // Image offset from the cursor
  const [currentPage, setCurrentPage] = useState(0); // Track the current page index

  const canvasRef = useRef(null);
  const contextRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight - 100;

    contextRef.current = canvas.getContext("2d");
    contextRef.current.lineCap = "round";
    contextRef.current.lineJoin = "round";
  }, []);

  const redrawCanvas = () => {
    const context = contextRef.current;
    const currentElements = elements[currentPage] || []; // Ensure it's an array if undefined
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    currentElements.forEach((element) => {
      if (element.type === "path") {
        context.strokeStyle = element.color;
        context.lineWidth = element.size;
        context.beginPath();
        context.moveTo(element.path[0].x, element.path[0].y);

        for (let i = 1; i < element.path.length; i++) {
          context.lineTo(element.path[i].x, element.path[i].y);
        }

        context.stroke();
      } else if (element.type === "rectangle") {
        context.strokeStyle = element.color;
        context.lineWidth = element.size;
        context.strokeRect(
          element.start.x,
          element.start.y,
          element.end.x - element.start.x,
          element.end.y - element.start.y
        );
      } else if (element.type === "circle") {
        const radius = Math.sqrt(
          Math.pow(element.end.x - element.start.x, 2) +
            Math.pow(element.end.y - element.start.y, 2)
        );
        context.strokeStyle = element.color;
        context.lineWidth = element.size;
        context.beginPath();
        context.arc(element.start.x, element.start.y, radius, 0, 2 * Math.PI);
        context.stroke();
      } else if (element.type === "image") {
        context.drawImage(
          element.image,
          element.position.x,
          element.position.y,
          element.width,
          element.height
        );
      }
    });
  };

  useEffect(() => {
    redrawCanvas();
  }, [elements, currentPage]);

  const exportToPNG = () => {
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = dataURL;
    link.download = "whiteboard.png";
    link.click();
  };

  const exportToSVG = () => {
    let svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="500" height="500">';
    elements.forEach((element) => {
      if (element.type === "path") {
        svg += `<path d="M${element.path[0].x} ${element.path[0].y} `;
        for (let i = 1; i < element.path.length; i++) {
          svg += `L${element.path[i].x} ${element.path[i].y} `;
        }
        svg += `" stroke="${element.color}" stroke-width="${element.size}" fill="none" />`;
      } else if (element.type === "rectangle") {
        svg += `<rect x="${element.start.x}" y="${element.start.y}" width="${
          element.end.x - element.start.x
        }" height="${element.end.y - element.start.y}" stroke="${
          element.color
        }" stroke-width="${element.size}" fill="none" />`;
      } else if (element.type === "circle") {
        const radius = Math.sqrt(
          Math.pow(element.end.x - element.start.x, 2) +
            Math.pow(element.end.y - element.start.y, 2)
        );
        svg += `<circle cx="${element.start.x}" cy="${element.start.y}" r="${radius}" stroke="${element.color}" stroke-width="${element.size}" fill="none" />`;
      }
    });
    svg += "</svg>";
    const link = document.createElement("a");
    link.href = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
    link.download = "whiteboard.svg";
    link.click();
  };

  const captureScreen = () => {
    html2canvas(canvasRef.current).then((canvas) => {
      // Use canvasRef directly
      const imageData = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = imageData;
      link.download = "whiteboard-capture.png";
      link.click();
    });
  };

  const getCursorPosition = (e) => {
    if (!e) return { x: 0, y: 0 }; // Prevent error when event is not passed
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    if (mode === "freehand") {
      setDrawing(true);
      const pos = getCursorPosition(e);
      setElements((prev) => {
        const newElements = [...prev];
        newElements[currentPage] = [
          ...newElements[currentPage],
          { type: "path", color: brushColor, size: brushSize, path: [pos] },
        ];
        return newElements;
      });
    } else {
      setStartPos(getCursorPosition(e));
    }
  };

  const draw = (e) => {
    if (!drawing || mode !== "freehand") return;

    const pos = getCursorPosition(e);
    setElements((prev) => {
      const newElements = [...prev];
      const currentPath =
        newElements[currentPage][newElements[currentPage].length - 1].path;
      currentPath.push(pos);
      return newElements;
    });
  };

  const stopDrawing = (e) => {
    if (mode === "freehand") {
      setDrawing(false);
    } else if (startPos) {
      const endPos = getCursorPosition(e);
      setElements((prev) => {
        const newElements = [...prev];
        newElements[currentPage] = [
          ...newElements[currentPage],
          {
            type: mode,
            color: brushColor,
            size: brushSize,
            start: startPos,
            end: endPos,
          },
        ];
        return newElements;
      });
      setStartPos(null);
    }
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const img = new Image();
      img.onload = () => {
        setElements((prev) => {
          const newElements = [...prev];
          newElements[currentPage] = [
            ...newElements[currentPage],
            {
              type: "image",
              image: img,
              position: { x: 100, y: 100 },
              width: 150,
              height: 150,
            },
          ];
          return newElements;
        });
      };
      img.src = URL.createObjectURL(file);
    }
  };

  // Move the selected image
  const handleMouseDown = (e) => {
    const pos = getCursorPosition(e);

    // Check if currentPage is within bounds
    if (currentPage < 0 || currentPage >= elements.length) {
      console.error("Invalid page index:", currentPage);
      return;
    }

    const pageElements = elements[currentPage];

    // Ensure pageElements is an array
    if (!Array.isArray(pageElements)) {
      console.error("Invalid or undefined page elements:", pageElements);
      return;
    }

    // Find image element
    const image = pageElements.find(
      (el) =>
        el.type === "image" &&
        pos.x >= el.position.x &&
        pos.x <= el.position.x + el.width &&
        pos.y >= el.position.y &&
        pos.y <= el.position.y + el.height
    );

    if (image) {
      setSelectedImage(image);
      setDraggingImage(true);
      setImageOffset({
        x: pos.x - image.position.x,
        y: pos.y - image.position.y,
      });
    } else {
      startDrawing(e);
    }
  };

  const handleMouseMove = (e) => {
    if (draggingImage && selectedImage) {
      const pos = getCursorPosition(e);
      const updatedPosition = {
        x: pos.x - imageOffset.x,
        y: pos.y - imageOffset.y,
      };
      setElements((prev) =>
        prev.map((el) =>
          el === selectedImage ? { ...el, position: updatedPosition } : el
        )
      );
    } else {
      draw(e);
    }
  };

  const handleMouseUp = (e) => {
    if (draggingImage) {
      setDraggingImage(false);
      setSelectedImage(null);
    } else {
      stopDrawing(e);
    }
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, elements.length - 1));
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const addPage = () => {
    setElements((prev) => [...prev, []]);
    setCurrentPage(elements.length); // Move to the newly created page
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <div
        className="whiteboard-Area"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={stopDrawing}
      >
        <h2 className="heading-whiteboard">
          <u>
            <strong>White Board</strong>
          </u>
        </h2>

        <div className="toolbar">
          <button onClick={exportToPNG}>Export as PNG</button>
          <button onClick={captureScreen}>Capture Screen</button>
          <button onClick={goToPreviousPage}>Previous Page</button>
          <button onClick={goToNextPage}>Next Page</button>
          <button onClick={addPage}>Add New Page</button>
        </div>

        <div className="controls">
          <div className="draw-controls">
            <label>Brush Size:</label>
            <input
              type="range"
              min="1"
              max="50"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
            />
            <label>Color Picker:</label>
            <input
              type="color"
              value={brushColor}
              onChange={(e) => setBrushColor(e.target.value)}
            />
          </div>

          <div className="shape-controls">
            <label>Drawing Mode:</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="freehand">Freehand</option>
              <option value="rectangle">Rectangle</option>
              <option value="circle">Circle</option>
              <option value="line">Line</option>
            </select>
          </div>

          <div className="image-upload">
            <label>Upload Image:</label>
            <input type="file" accept="image/*" onChange={handleImageUpload} />
          </div>
        </div>

        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          className="canvas"
        ></canvas>
      </div>
      <Footer />
    </>
  );
};

export default Whiteboard;
